import { examples as manifest } from '@portal/pkg/examples/manifest';

export interface ExampleSource {
	language: string;

	source: string;
}

type RawSource = string | { default: string };

type ExampleLoaders = Record<string, () => Promise<RawSource>>;

const registry: Record<string, Record<string, ExampleLoaders>> = manifest;

// The dev server registers `?raw` text as CJS, whose dynamic import resolves
// to the string itself; the production bundle resolves to a namespace.
function text(module: RawSource): string {
	return typeof module === 'string' ? module : module.default;
}

const languageOrder = ['sh', 'go', 'js', 'java'];

function rank(language: string): number {
	const index = languageOrder.indexOf(language);

	return index === -1 ? languageOrder.length : index;
}

export function hasExample(key: string, name: string): boolean {
	return Boolean(registry[key]?.[name]);
}

/**
 *	`use()` requires the same promise across renders, so each load is kept,
 *	and a rejection is evicted so a transient chunk fetch error is retried.
 */
const sources: Map<string, Promise<ExampleSource[]>> = new Map();

if (import.meta.hot) {
	import.meta.hot.on('bun:afterUpdate', () => {
		sources.clear();
	});
}

export function exampleSources(
	key: string,
	name: string
): Promise<ExampleSource[]> {
	const loaders = registry[key]?.[name];

	if (!loaders) {
		return Promise.reject(new Error(`No example ${name} for ${key}.`));
	}

	const cacheKey = `${key}/${name}`;
	const existing = sources.get(cacheKey);

	if (existing) {
		return existing;
	}

	const pending = Promise.all(
		Object.entries(loaders)
			.toSorted(
				([first], [second]) =>
					rank(first) - rank(second) || first.localeCompare(second)
			)
			.map(async ([language, load]) => ({
				language,
				source: text(await load()),
			}))
	);

	sources.set(cacheKey, pending);
	pending.catch(() => sources.delete(cacheKey));

	return pending;
}
